import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Answer } from '@/types/workerInfo.types';
import { colors, spacing } from '@/theme';
import { Check, Plus } from 'lucide-react';

interface MultipleChoiceInputProps {
  answer: Answer;
  options: string[];
  onBlur: (value: { arrayValue: string[] }) => void;
  disabled?: boolean;
}

export function MultipleChoiceInput({ answer, options, onBlur, disabled }: MultipleChoiceInputProps) {
  const [selected, setSelected] = useState<string[]>(answer.arrayValue || []);
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    setSelected(answer.arrayValue || []);
  }, [answer.arrayValue]);

  const handleToggle = (option: string) => {
    if (disabled) return;

    let newSelected: string[];
    if (selected.includes(option)) {
      newSelected = selected.filter(item => item !== option);
    } else {
      newSelected = [...selected, option];
    }
    
    setSelected(newSelected);
    
    // Guardar al cambiar selección
    onBlur({ arrayValue: newSelected });
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  const handleSelectAll = () => {
    if (disabled) return;
    
    if (selected.length === options.length) {
      setSelected([]);
      onBlur({ arrayValue: [] });
    } else {
      setSelected([...options]);
      onBlur({ arrayValue: [...options] });
    }
    setIsSaved(true);
    setTimeout(() => setIsSaved(false), 2000);
  };

  return (
    <div
      style={{
        padding: spacing[4],
        backgroundColor: colors.white,
        borderRadius: 16,
        border: `1px solid ${colors.gray[200]}`,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: spacing[3],
        }}
      >
        <div>
          <label
            style={{
              fontSize: 16,
              fontWeight: 600,
              color: colors.gray[900],
              display: 'flex',
              alignItems: 'center',
              gap: spacing[2],
              marginBottom: spacing[1],
            }}
          >
            <Plus size={16} color={colors.primary[500]} />
            {answer.question.question}
            {answer.question.isRequired && (
              <span style={{ color: colors.error?.[500] || '#ef4444', marginLeft: 4 }}>*</span>
            )}
          </label>
          {answer.question.description && (
            <p style={{ fontSize: 14, color: colors.gray[600], marginBottom: spacing[2] }}>
              {answer.question.description}
            </p>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: spacing[2] }}>
          <AnimatePresence>
            {isSaved && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing[1],
                  color: colors.success?.[500] || '#10b981',
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                <Check size={16} />
                <span>Guardado</span>
              </motion.div>
            )}
          </AnimatePresence>
          
          <button
            onClick={handleSelectAll}
            disabled={disabled}
            style={{
              padding: `${spacing[1]} ${spacing[2]}`,
              fontSize: 12,
              color: colors.primary[600],
              backgroundColor: colors.primary[50],
              border: `1px solid ${colors.primary[200]}`,
              borderRadius: 6,
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            {selected.length === options.length ? 'Deseleccionar todo' : 'Seleccionar todo'}
          </button>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
          gap: spacing[2],
          marginTop: spacing[2],
        }}
      >
        {options.map((option, index) => {
          const isSelected = selected.includes(option);
          
          return (
            <motion.button
              key={option}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.02 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleToggle(option)}
              disabled={disabled}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing[2],
                padding: spacing[2],
                borderRadius: 10,
                border: `2px solid ${
                  isSelected ? colors.primary[500] : colors.gray[200]
                }`,
                backgroundColor: isSelected ? colors.primary[50] : colors.white,
                color: isSelected ? colors.primary[700] : colors.gray[700],
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
                opacity: disabled ? 0.6 : 1,
                fontSize: 14,
              }}
            >
              <div
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 4,
                  border: `2px solid ${
                    isSelected ? colors.primary[500] : colors.gray[300]
                  }`,
                  backgroundColor: isSelected ? colors.primary[500] : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isSelected && <Check size={12} color="white" />}
              </div>
              {option}
            </motion.button>
          );
        })}
      </div>

      <div
        style={{
          marginTop: spacing[3],
          padding: spacing[2],
          backgroundColor: colors.primary[50],
          borderRadius: 8,
          fontSize: 12,
          color: colors.primary[700],
          textAlign: 'center',
        }}
      >
        {selected.length} {selected.length === 1 ? 'opción seleccionada' : 'opciones seleccionadas'}
      </div>
    </div>
  );
}